import StockAvatar from '@/components/ui/StockAvatar'
import SymbolLink from '@/components/ui/SymbolLink'
import { SYMBOL_LINK_HOVER } from '@/components/ui/symbol-link'
import { cn } from '@/lib/utils'

interface SymbolCellProps {
  symbol: string
  name: string
  /** The row's own `logo_url`, when its endpoint sends one. Passed straight
   *  through as `StockAvatar`'s `logoUrl`, which tries it only after the CDN
   *  logo it builds from the symbol — so a row with none still gets a logo. */
  logo?: string
  /**
   * The ticker the mark is drawn for, when the top line is not one — an
   * alert's title ("NABIL price alert", or the member's own label). Defaults
   * to `symbol`. Without it the mark would build its CDN URL out of the
   * title and 404 on every row.
   */
  markSymbol?: string
  /**
   * What the mark's letter tile spells when no logo loads — `StockAvatar`'s
   * own `monogram`. Defaults to the ticker's first letter; Signal Zone's
   * Company column passes the ticker's first two (its frames draw "MP" for
   * MPFL).
   */
  monogram?: string
  /**
   * Reserves the mark slot on EVERY row, even when `logo` is absent — the
   * CDN logo built from the symbol fills it, or a same-size tile carrying the
   * ticker's first letter when the CDN has none, so every ticker/name pair
   * still lines up under one x-offset. Without it, a logo-less row's text
   * sits one tile width left of a logoed row's — the AVYAN bug this exists
   * to fix. Leave it off on a table whose design draws no mark at all; `logo`
   * alone already omits the tile cleanly there.
   */
  reserveMark?: boolean
  /**
   * Draws the mark at 28 with a 4px muted ring around a 20 white plate — the
   * Holdings table's symbol cell (owner's target, `mm-pass-2026-10-23`) —
   * instead of the bare plate at the size's own 24/32. A letter mark keeps its
   * muted disc, so the ring simply merges into it. Off everywhere else.
   */
  ringedMark?: boolean
  /** `sm` tightens the pair, and shrinks the mark to 24, for the dense tables. */
  size?: 'sm' | 'md'
  /**
   * `signal` (sz:sb, the Signal Zone mock's company cell, `COMPARE-sz2` S6): a 36 mark whose letter
   * is 14/600, and the company name in the secondary grey instead of the quieter one. Everything
   * else is the default pair. Additive: no other table passes it.
   * `alert` (rest:al, the Alerts mock's row mark): the same 36 mark and 14 letter, with the
   * default pair's quieter company name.
   */
  look?: 'signal' | 'alert'
  /**
   * Makes the whole pair (logo, ticker, name) one link to `/stocks/$symbol`,
   * underlining only the ticker on hover. Leave it off inside a DataTable
   * that already uses `rowLinkOptions` — that row is the link already.
   */
  linked?: boolean
  className?: string
}

/**
 * The leading cell of every Market table with a symbol/company column: ticker
 * over company name, with the company's logo in the mark slot when the table
 * draws one (`reserveMark`, or a `logo`). V2's symbol cell (`4236:69879`,
 * `4236:104328`): the logo on a 32 white plate, the ticker 14/600 and the name
 * 12 in the quieter grey, 12 apart.
 *
 * The mark is decorative — `alt=""`, and the fallback letter is `aria-hidden`
 * the same way (`StockAvatar`'s own default) — because the symbol is printed
 * right beside it. Naming the logo too would make a screen reader read every
 * row twice.
 *
 * The name truncates rather than wraps: a wrapping second line would make one
 * long company name taller than every other row in the column. Truncation
 * only actually STOPS the column growing when the enclosing `DataTable`
 * column carries `meta.fixedWidth` — `min-w-0` plus `truncate` needs a
 * genuinely constrained ancestor box to shrink inside, which is what that
 * meta field, not this component, is responsible for providing (see its
 * JSDoc in `DataTable.tsx`). `title` on each line surfaces the untruncated
 * text on hover; the full string is already in the DOM either way, so a
 * screen reader reads it regardless of how it is clipped visually.
 */
export default function SymbolCell({
  symbol,
  name,
  logo,
  markSymbol,
  monogram,
  reserveMark = false,
  ringedMark = false,
  size = 'md',
  look,
  linked = false,
  className,
}: SymbolCellProps) {
  const rootClass = cn('flex items-center', size === 'sm' ? 'gap-2' : 'gap-3', className)
  // Same gate `IconTile` used to sit behind: a mark slot draws only when the
  // caller supplied a logo or asked to reserve one — a table whose design has
  // no mark at all keeps drawing none.
  const mark =
    logo !== undefined || reserveMark ? (
      // `exactOptionalPropertyTypes` refuses an explicit `logoUrl={undefined}`
      // — StockAvatar's `logoUrl` is `string | null`, not `| undefined` — so
      // the key is omitted rather than handed through when `logo` is absent.
      <StockAvatar
        symbol={markSymbol ?? symbol}
        name={name}
        size={ringedMark ? 28 : look !== undefined ? 36 : size === 'sm' ? 'xs' : 'sm'}
        // The logo fills the 20 plate inside the 4px ring (`p-0`: the logo
        // files carry their own margin), and the ring replaces the hairline
        // the plate otherwise wears in light.
        {...(ringedMark
          ? { className: 'border-4 border-muted p-0 ring-0' }
          : look !== undefined
            ? // The letter is the avatar's inner span: one step up from its 12px box text.
              { className: '[&>span]:text-label-l' }
            : {})}
        {...(logo === undefined ? {} : { logoUrl: logo })}
        {...(monogram === undefined ? {} : { monogram })}
      />
    ) : null
  const body = (
    <>
      {mark}
      <span className="min-w-0">
        {/* `data-row-name`: the line a DataTable link row underlines on hover and focus. */}
        <span
          title={symbol}
          data-row-name=""
          className={cn(
            'block truncate',
            // The dense pair keeps the 14/600 ticker on the label's shorter line.
            size === 'sm' ? 'text-label-l font-semibold' : 'text-h-s',
            linked ? SYMBOL_LINK_HOVER : null,
          )}
        >
          {symbol}
        </span>
        <span
          title={name}
          className={cn(
            'block truncate text-body-xs',
            look === 'signal' ? 'text-muted-foreground' : 'text-muted-foreground-subtle',
          )}
        >
          {name}
        </span>
      </span>
    </>
  )

  return linked ? (
    <SymbolLink symbol={symbol} underline="group" className={rootClass}>
      {body}
    </SymbolLink>
  ) : (
    <span className={rootClass}>{body}</span>
  )
}
